import fetchIntercept from 'fetch-intercept';
import { matchPath } from 'react-router-dom';
import Environment from '@eatzy/common-reactjs/Environment';
import { EXPIRED_TOKEN } from '../../constants/auth';
import { UNAUTHORIZED } from '../../constants/http';
import { logoutRequest } from '../../redux/ducks/UserDucks';
import Authentication from '../../utils/authentication';
import { jsonHeaders } from '../JsonHeaders';

export const authorizationTokenRoutes = [
  { method: 'post', endpoint: '/account/authorize' },
  { method: 'post', endpoint: '/account/refresh' },
  { method: 'post', endpoint: '/account/deauthorize' },
];

const isDeliveryIssuesPath = (path) =>
  /^\/deliveryops\/(?:orders\/[1-9]\d*\/)?delivery-issues\/?$/.test(path);
const isDispatcherSoundPath = (path) =>
  /^\/deliveryops\/(?:alert-events|me\/alert-preferences)\/?$/.test(path);

const matchesRoute = ({ url, method }, endpoints) => {
  const matchesMethod = endpoints.some(
    (route) => route.method.toLowerCase() === method.toLowerCase()
  );

  const matchesURL = endpoints.some((route) =>
    matchPath(url.pathname, {
      path: route.endpoint,
      exact: true,
      strict: false,
    })
  );

  return !!(matchesMethod && matchesURL);
};

export const insertUseRandomFakeData = async (url, config) => {
  const useFakeData = Environment.getEnv('API_FAKE_MODE');

  const isCalendarHistory = /\/deliveryops\/drivers\/history\/v2(?:\/|$)/.test(
    new URL(url, window.location.origin).pathname
  );
  const isDirectory = /\/deliveryops\/drivers\/directory\/?$/.test(
    new URL(url, window.location.origin).pathname
  );
  const isFundedTip = /\/deliveryops\/orders\/(?:\d+\/)?(?:tip-adjustments|tip-adjustment-policy)(?:\/|$)/.test(
    new URL(url, window.location.origin).pathname
  );
  const isCoverage = /\/deliveryops\/(?:territory-coverage|businesses\/options)\/?$/.test(
    new URL(url, window.location.origin).pathname
  );
  const isDeliveryIssues = isDeliveryIssuesPath(new URL(url, window.location.origin).pathname);
  const isSound = isDispatcherSoundPath(new URL(url, window.location.origin).pathname);
  if (useFakeData === 'true' && config?.headers && !isCalendarHistory && !isDirectory && !isFundedTip && !isCoverage && !isDeliveryIssues && !isSound) {
    config.headers = {
      ...config.headers,
      'X-API-FakeData': 'random',
    };
  }

  return [url, config];
};

export const insertBasicHeaders = async (url, config) => {
  config.headers = {
    ...jsonHeaders(config.headers),
    'X-App-Version': process.env.REACT_APP_VERSION,
    'X-App-Source': 'delivery-operations-web',
    'X-App-OS':
      window?.navigator?.platform ||
      window?.navigator?.userAgentData?.platform ||
      'unknown',
  };

  return [url, config];
};

export const insertAuthorizationTokenInRequest = async (url, config) => {
  if (config.hasRefreshToken) {
    config.headers = {
      ...config.headers,
      authorization: `Basic ${config.refreshToken}`,
    };
  }

  return [url, config];
};

export const insertAccessTokenInRequest = async (url, config, store) => {
  const authToken = await Authentication.getToken();

  if (!authToken) {
    return [url, config];
  }
  if (authToken?.isExpired) {
    if (store) store.dispatch(logoutRequest(EXPIRED_TOKEN));
    return [url, config];
  }

  config.headers = {
    ...config.headers,
    authorization: `Bearer ${authToken?.token}`,
  };

  return [url, config];
};

export const interceptors = (store) => ({
  request(url, config) {
    insertBasicHeaders(url, config);
    insertUseRandomFakeData(url, config);

    // Modify the url or config here
    const refreshRelated = matchesRoute(
      { url, method: config.method },
      authorizationTokenRoutes
    );

    if (refreshRelated || config?.hasRefreshToken) {
      return insertAuthorizationTokenInRequest(url, config, store);
    }
    return insertAccessTokenInRequest(url, config, store);
  },

  requestError(error) {
    // Called when an error occured during another 'request' interceptor call
    return Promise.reject(error);
  },

  async response(response) {
    if (isDispatcherSoundPath(new URL(response.url || '/', window.location.origin).pathname)) {
      const data = await response.json().catch(() => null);
      if (response.ok) return data;
      throw Object.assign(new Error(typeof data?.message === 'string' ? data.message : 'Dispatcher alerts request failed'), {
        status: response.status,
        request_id: typeof data?.request_id === 'string' ? data.request_id : 'unknown',
      });
    }
    if (isDeliveryIssuesPath(new URL(response.url || '/', window.location.origin).pathname)) {
      const data = await response.json().catch(() => null);
      if (response.ok) return data;
      throw Object.assign(new Error(typeof data?.message === 'string' ? data.message : 'Delivery issues request failed'), {
        status: response.status,
        request_id: typeof data?.request_id === 'string' ? data.request_id : 'unknown',
      });
    }
    if (/\/deliveryops\/drivers\/history\/v2(?:\/|$)/.test(
      new URL(response.url || '/', window.location.origin).pathname
    )) {
      const data = await response.json().catch(() => null);
      if (response.ok) return data;
      throw Object.assign(new Error(data?.message || 'History request failed'), { status: response.status });
    }
    if (/\/deliveryops\/(?:territory-coverage|businesses\/options)\/?$/.test(
      new URL(response.url || '/', window.location.origin).pathname
    )) {
      const data = await response.json().catch(() => null);
      if (response.ok) return data;
      const error = new Error(typeof data?.message === 'string' ? data.message : 'Coverage scope request failed');
      error.status = response.status;
      error.request_id = typeof data?.request_id === 'string' ? data.request_id : 'unknown';
      throw error;
    }
    // Financial errors retain HTTP status and stable machine codes. A malformed
    // response must reject, not leave the request permanently unresolved.
    if (/\/deliveryops\/orders\/(?:\d+\/)?(?:tip-adjustments|tip-adjustment-policy)(?:\/|$)/.test(
      new URL(response.url || '/', window.location.origin).pathname
    )) {
      const data = await response.json().catch(() => null);
      if (response.ok && data && typeof data === 'object') return data;
      const error = new Error('Funded tip request failed');
      Object.assign(error, data && typeof data === 'object' ? data : {}, { status: response.status });
      throw error;
    }
    // Preserve directory HTTP errors without logging contact/residential payloads.
    if (/\/deliveryops\/drivers\/directory\/?$/.test(
      new URL(response.url || '/', window.location.origin).pathname
    )) {
      const data = await response.json().catch(() => null);
      if (response.ok) return data;
      const error = new Error(
        data?.message === 'BUSINESS_TIMEZONE_UNAVAILABLE'
          ? data.message : 'Directory request failed'
      );
      error.status = response.status;
      throw error;
    }
    // Modify the reponse object
    return new Promise(async (resolve, reject) => {
      if (response.status === UNAUTHORIZED) {
        reject({ message: 'Unauthorized', status: 401 });
      }
      if (response.status === 403) {
        reject({ message: 'Forbidden', status: 403 });
      }
      const data = await response.json();
      if (response.ok) {
        resolve(data);
      } else {
        reject(data);
      }
    });
  },

  responseError(error) {
    // Handle an fetch error
    return Promise.reject(error);
  },
});

export default (store) => fetchIntercept.register(interceptors(store));
